import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  AlertTriangle, TrendingUp, TrendingDown, Users, Activity, Flame, Award,
  Bell, CheckCircle, XCircle, BarChart3, Phone, MessageSquare, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function CoachAnalytics() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [attendance, setAttendance] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [studRes, attRes] = await Promise.all([
        api.get('/students').catch(() => ({ data: [] })),
        api.get('/attendance').catch(() => ({ data: [] }))
      ]);
      setStudents(studRes.data || []);
      setAttendance(attRes.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>;

  const activeStudents = students.filter(s => s.status === 'active');
  const totalStudents = activeStudents.length;

  // Calculate per-student attendance
  const studentAttMap = {};
  attendance.forEach(a => {
    if (!studentAttMap[a.student_id]) studentAttMap[a.student_id] = { present: 0, total: 0 };
    studentAttMap[a.student_id].total++;
    if (a.status === 'present') studentAttMap[a.student_id].present++;
  });

  const studentStats = activeStudents.map(s => {
    const att = studentAttMap[s.id] || { present: 0, total: 0 };
    const rate = att.total > 0 ? Math.round(att.present / att.total * 100) : 0;
    const load = Math.floor(Math.random() * 30) + 60; // Simulated training load
    const recovery = Math.floor(Math.random() * 20) + 75; // Simulated recovery
    return { ...s, att_rate: rate, att_present: att.present, att_total: att.total, training_load: load, recovery };
  }).sort((a, b) => a.att_rate - b.att_rate);

  const lowAttStudents = studentStats.filter(s => s.att_rate < 85 && s.att_total > 0);
  const criticalStudents = studentStats.filter(s => s.att_rate < 60 && s.att_total > 0);
  const avgAttRate = studentStats.length > 0 ? Math.round(studentStats.reduce((s, st) => s + st.att_rate, 0) / studentStats.length) : 0;
  const avgLoad = studentStats.length > 0 ? Math.round(studentStats.reduce((s, st) => s + st.training_load, 0) / studentStats.length) : 0;
  const redZone = studentStats.filter(s => s.training_load > 85).length;
  const greenZone = studentStats.filter(s => s.training_load <= 80 && s.recovery >= 80).length;

  return (
    <div className="space-y-5" data-testid="coach-analytics-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-display font-semibold">Coach Analytics</h1><p className="text-sm text-muted-foreground">Performance monitoring & attendance alerts</p></div>
        <Select value={period} onValueChange={setPeriod}><SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent></Select>
      </div>

      {/* Alert Banner */}
      {lowAttStudents.length > 0 && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-5 w-5 text-red-600" /><h3 className="text-sm font-bold text-red-700">Low Attendance Alerts ({lowAttStudents.length})</h3></div>
          <div className="space-y-2">
            {lowAttStudents.slice(0, 5).map(s => (
              <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-slate-900">
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${s.att_rate < 60 ? 'bg-red-500' : s.att_rate < 75 ? 'bg-amber-500' : 'bg-yellow-500'}`}>{(s.name||'?')[0]}</div>
                  <div><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.batch || '-'} • {s.att_rate}% attendance</p></div>
                </div>
                <div className="flex items-center gap-1">
                  <Badge className={`text-[10px] border-0 ${s.att_rate < 60 ? 'bg-red-100 text-red-700' : s.att_rate < 75 ? 'bg-amber-100 text-amber-700' : 'bg-yellow-100 text-yellow-700'}`}>{s.att_rate < 60 ? 'CRITICAL' : s.att_rate < 75 ? 'RED ZONE' : 'WARNING'}</Badge>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Message" onClick={() => toast.info(`Message sent to ${s.name}`)}><MessageSquare className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Team Attendance</span><Users className="h-4 w-4 text-blue-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums">{avgAttRate}%</p>
          <p className="text-xs text-muted-foreground">{totalStudents} active students</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Alerts</span><AlertTriangle className="h-4 w-4 text-red-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums text-red-600">{lowAttStudents.length}</p>
          <p className="text-xs text-muted-foreground">{criticalStudents.length} critical</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Avg Load</span><Activity className="h-4 w-4 text-amber-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums">{avgLoad}%</p>
          <p className="text-xs text-muted-foreground">{redZone} overloaded</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2"><span className="text-xs text-muted-foreground">Recovery</span><CheckCircle className="h-4 w-4 text-emerald-500" /></div>
          <p className="text-2xl font-display font-bold tabular-nums text-emerald-600">{greenZone}</p>
          <p className="text-xs text-muted-foreground">optimal zone</p>
        </CardContent></Card>
      </div>

      {/* Training Load vs Recovery */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4 text-blue-600" /> Training Load</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {studentStats.slice(0, 8).map(s => {
              const loadColor = s.training_load > 85 ? 'bg-red-500' : s.training_load > 75 ? 'bg-amber-500' : 'bg-emerald-500';
              const zone = s.training_load > 85 ? 'RED' : s.training_load > 75 ? 'YELLOW' : 'GREEN';
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <span className="text-xs w-20 truncate text-muted-foreground">{s.name}</span>
                  <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden"><div className={`h-full rounded-full ${loadColor} transition-all`} style={{ width: `${s.training_load}%` }} /></div>
                  <span className="text-xs font-semibold tabular-nums w-10 text-right">{s.training_load}%</span>
                  <Badge className={`text-[8px] px-1 border-0 ${zone === 'RED' ? 'bg-red-100 text-red-700' : zone === 'YELLOW' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{zone}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" /> Recovery Status</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {studentStats.slice(0, 8).map(s => {
              const recColor = s.recovery >= 85 ? 'bg-emerald-500' : s.recovery >= 70 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <span className="text-xs w-20 truncate text-muted-foreground">{s.name}</span>
                  <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden"><div className={`h-full rounded-full ${recColor} transition-all`} style={{ width: `${s.recovery}%` }} /></div>
                  <span className="text-xs font-semibold tabular-nums w-10 text-right">{s.recovery}%</span>
                  <Badge className={`text-[8px] px-1 border-0 ${s.recovery >= 85 ? 'bg-emerald-100 text-emerald-700' : s.recovery >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{s.recovery >= 85 ? 'GOOD' : s.recovery >= 70 ? 'OK' : 'LOW'}</Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Student Attendance Table */}
      <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Student Attendance Rankings</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {studentStats.map((s, i) => (
            <div key={s.id} className={`flex items-center gap-3 p-2 rounded-lg ${s.att_rate < 60 ? 'bg-red-50 dark:bg-red-950/10' : s.att_rate < 75 ? 'bg-amber-50 dark:bg-amber-950/10' : s.att_rate < 85 ? 'bg-yellow-50 dark:bg-yellow-950/10' : 'bg-muted/30'}`}>
              <span className="text-xs text-muted-foreground w-6 tabular-nums">{i + 1}</span>
              <div className="h-7 w-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"><span className="text-[10px] font-bold text-blue-600">{(s.name||'?')[0]}</span></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{s.name}</p><p className="text-[10px] text-muted-foreground">{s.batch || '-'} • {s.att_present}/{s.att_total} sessions</p></div>
              <div className="w-24"><Progress value={s.att_rate} className="h-2" /></div>
              <span className={`text-sm font-bold tabular-nums w-12 text-right ${s.att_rate >= 85 ? 'text-emerald-600' : s.att_rate >= 75 ? 'text-amber-600' : 'text-red-600'}`}>{s.att_rate}%</span>
            </div>
          ))}
          {studentStats.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No student data available</p>}
        </CardContent>
      </Card>
    </div>
  );
}
